/**
 * MobilePickerOverlayView - Mobile action picker on the canonical menu (SE-5546)
 *
 * Renders the player-detail "+" picker through `MobileActionMenu`, listing
 * "Add a report", "Add to list", and for manual players "Edit player",
 * "Transfer reports & notes" (disabled when 0 reports and 0 notes) and
 * "Delete player" (destructive, always last). The former bespoke Actionsheet
 * chrome and the six isDark class dictionaries are gone — semantic tokens
 * come from the molecule.
 * Used by MobileActionBarView when on small screens.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import {
  ArrowRightLeft,
  FileText,
  ListPlus,
  Pencil,
  Trash2,
} from "lucide-react-native";
import { memo } from "react";

import MobileActionMenu, {
  type MobileActionMenuItem,
} from "@/components/molecules/MobileActionMenu";

/**
 * Props for MobilePickerOverlayView
 */
export interface MobilePickerOverlayViewProps {
  /** Whether the picker is open */
  readonly isPickerOpen: boolean;
  /** Callback to close the picker */
  readonly onPickerClose: () => void;
  /** Callback for "Add a report" option (hidden if undefined) */
  readonly onAddReport?: () => void;
  /** Callback for "Add to list" option (hidden if undefined) */
  readonly onAddToList?: () => void;
  /** Callback for "Edit player" option — manual players only (hidden if undefined) */
  readonly onEditPlayer?: () => void;
  /** Callback for "Delete player" option — manual players only (hidden if undefined) */
  readonly onDeletePlayer?: () => void;
  /** Callback for "Transfer reports & notes" — admin users on manual players only (hidden if undefined) */
  readonly onTransferReports?: () => void;
  /** Whether the transfer reports action is disabled (0 reports and 0 notes) */
  readonly isTransferDisabled?: boolean;
}

/**
 * Builds the ordered menu items from the optional callbacks. An undefined
 * callback hides its row — mirroring the pre-migration conditional rendering.
 * @param props - The picker's view props (only the item callbacks are read)
 * @param props.onAddReport - "Add a report" callback (row hidden if undefined)
 * @param props.onAddToList - "Add to list" callback (row hidden if undefined)
 * @param props.onEditPlayer - "Edit player" callback (row hidden if undefined)
 * @param props.onDeletePlayer - "Delete player" callback (row hidden if undefined)
 * @param props.onTransferReports - "Transfer reports & notes" callback (row hidden if undefined)
 * @param props.isTransferDisabled - Whether the transfer row is inert
 * @returns Menu items in the historical order (add pair, then management)
 */
const buildPickerItems = ({
  onAddReport,
  onAddToList,
  onEditPlayer,
  onDeletePlayer,
  onTransferReports,
  isTransferDisabled,
}: MobilePickerOverlayViewProps): readonly MobileActionMenuItem[] => [
  ...(onAddReport
    ? [
        {
          icon: FileText,
          label: "Add a report",
          onPress: onAddReport,
          testID: "mobile-picker-add-report",
        },
      ]
    : []),
  ...(onAddToList
    ? [
        {
          icon: ListPlus,
          label: "Add to list",
          onPress: onAddToList,
          testID: "mobile-picker-add-to-list",
        },
      ]
    : []),
  ...(onEditPlayer
    ? [
        {
          icon: Pencil,
          label: "Edit player",
          onPress: onEditPlayer,
          testID: "mobile-picker-edit-player",
        },
      ]
    : []),
  ...(onTransferReports
    ? [
        {
          icon: ArrowRightLeft,
          label: "Transfer reports & notes",
          onPress: onTransferReports,
          disabled: isTransferDisabled === true,
          testID: "mobile-picker-transfer-reports",
        },
      ]
    : []),
  ...(onDeletePlayer
    ? [
        {
          icon: Trash2,
          label: "Delete player",
          onPress: onDeletePlayer,
          destructive: true,
          testID: "mobile-picker-delete-player",
        },
      ]
    : []),
];

/**
 * MobilePickerOverlayView renders the "+" picker as a MobileActionMenu
 * @param props - View props for the picker overlay
 * @param props.isPickerOpen - Whether the picker is open
 * @param props.onPickerClose - Callback to close the picker
 * @param props.onAddReport - Callback for "Add a report"
 * @param props.onAddToList - Callback for "Add to list"
 * @param props.onEditPlayer - Callback for "Edit player" (manual players only)
 * @param props.onDeletePlayer - Callback for "Delete player" (manual players only)
 * @param props.onTransferReports - Callback for "Transfer reports & notes" (admin on manual players)
 * @param props.isTransferDisabled - Whether the transfer action is disabled (0 reports and 0 notes)
 * @returns The canonical mobile action menu with the picker's items
 */
const MobilePickerOverlayView = (props: MobilePickerOverlayViewProps) => (
  <MobileActionMenu
    isOpen={props.isPickerOpen}
    onClose={props.onPickerClose}
    title="Add"
    items={buildPickerItems(props)}
    testID="mobile-picker-overlay"
  />
);

MobilePickerOverlayView.displayName = "MobilePickerOverlayView";

export default memo(MobilePickerOverlayView);
